import React, { useEffect, useState } from "react";
import { Grid, TextField } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Divider from "@material-ui/core/Divider";
import { FilterBar } from "../../../components/Grid/FilterBar";
import { waitSingleton } from "../../../utils/wait";
import ParkingZoneRepository from "../../../repositories/ParkingZoneRepository";
import { DateRangeFilter } from "../../../components/DateRangeFilter";
import { format } from "date-fns";
import StaticClassifierRepository from "../../../repositories/StaticClassifierRepository";
import { HourRange } from "../../../components/HourRange";
import CameraRepository from "../../../repositories/CameraRepository";

const Filter = ({
  filter,
  onUpdateFilter,
  sort,
  onFilterChangedHandler,
  onSortChangedHandler,
}) => {
  const { t } = useTranslation();
  const [parkingZones, setParkingZones] = useState([]);
  const [suspiciousTypes, setSuspiciousTypes] = useState([]);
  const [showTime, setShowTime] = useState(false);
  const [directions, setDirections] = useState([]);
  const [cameras, setCameras] = useState([]);

  const sortOptions = [
    {
      value: "Id|desc",
      label: t("Newest first"),
    },
    {
      value: "Id|asc",
      label: t("Oldest first"),
    },
  ];

  const tabs = [
    { label: "All", value: "all" },
    { label: "Only suspicious", value: "suspicious" },
    { label: "Not paired", value: "not-paired" },
  ];

  const [currentTab, setCurrentTab] = useState(() => {
    if (filter.IsSuspicious) {
      return "suspicious";
    } else if (filter.NotPaired) {
      return "not-paired";
    }
    return "all";
  });

  const handleTabsChange = (event, value) => {
    setCurrentTab(value);

    const filters = {
      all: { IsSuspicious: null, NotPaired: null },
      suspicious: { IsSuspicious: true, NotPaired: null },
      "not-paired": { IsSuspicious: null, NotPaired: true },
    };

    waitSingleton(500).then(() => {
      if (onUpdateFilter) {
        onUpdateFilter(null, filters[value]);
      }
    });
  };

  useEffect(() => {
    const show = filter.DateFrom === filter.DateTo;
    setShowTime(show);
    if (showTime && !show) {
      onUpdateFilter(null, { HourFrom: null, HourTo: null });
    }
  }, [filter]);

  useEffect(async () => {
    const parkingZoneRepository = new ParkingZoneRepository();
    const result = await parkingZoneRepository.getSelector();
    setParkingZones(result.data);

    const cameraRepository = new CameraRepository();
    
    const classifierRepository = new StaticClassifierRepository();
    const result2 =  classifierRepository.getAllCameraEventSuspiciousStatuses();
    setSuspiciousTypes(result2);

    setDirections(classifierRepository.getAllMovingDirections());

    const camerasRs = await cameraRepository.getSelectorWithParkingZone();
    setCameras(camerasRs.data);
  }, []);

  return (
    <Grid>
      <Tabs
        onChange={handleTabsChange}
        value={currentTab}
        scrollButtons="auto"
        textColor="secondary"
        variant="scrollable"
      >
        {tabs.map((tab) => (
          <Tab key={tab.value} value={tab.value} label={tab.label} />
        ))}
      </Tabs>
      <Divider />
      <FilterBar
        showQuerySearch={true}
        sortOptions={sortOptions}
        onFilterChanged={onFilterChangedHandler}
        onSortChanged={onSortChangedHandler}
        filter={filter}
        sort={sort}
      >
        <Grid item xs={3} container spacing={1}>
          <Grid item xs>
            <DateRangeFilter
              value={
                filter.DateFrom && filter.DateTo
                  ? {
                      startDate: new Date(filter.DateFrom),
                      endDate: new Date(filter.DateTo),
                    }
                  : null
              }
              onChange={(e) => {
                onUpdateFilter(null, {
                  DateFrom: format(e.startDate, "yyyy-MM-dd"),
                  DateTo: format(e.endDate, "yyyy-MM-dd"),
                });
              }}
            />
          </Grid>
          {showTime && (
            <Grid item xs>
              <HourRange
                from={filter.HourFrom}
                to={filter.HourTo}
                onUpdate={(type, value) => {
                  onUpdateFilter(type == "from" ? "HourFrom" : "HourTo", value);
                }}
              />
            </Grid>
          )}
        </Grid>

        <Grid item xs={2}>
          <TextField
            label={t("Parking zone")}
            select
            fullWidth
            SelectProps={{ native: true }}
            value={filter.ParkingZoneId}
            onChange={(e) => onUpdateFilter("ParkingZoneId", e.target.value)}
          >
            <option></option>
            {parkingZones.map((item) => (
              <option key={item.Id} value={item.Id}>
                {item.Name}
              </option>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={2}>

          <TextField
              label={t("Camera")}
              select
              fullWidth
              SelectProps={{ native: true }}
              value={filter.CameraId}
              onChange={(e) => onUpdateFilter("CameraId", e.target.value)}
          >
            <option></option>
            {cameras.map((cameraGroup, i) => {
              return (
                  <optgroup key={i} label={cameraGroup.ParkingZoneName}>
                    {cameraGroup.Cameras.map((camera, j) => {
                      return (
                          <option key={camera.Id} value={camera.Id}>
                            {camera.Name}
                          </option>
                      );
                    })}
                  </optgroup>
              );
            })}
          </TextField>
        </Grid>
       
        <Grid item xs={1}>
          <TextField
              label={t("Direction")}
              select
              fullWidth
              SelectProps={{ native: true }}
              value={filter.Direction}
              onChange={(e) => onUpdateFilter("Direction", e.target.value)}
          >
            <option></option>
            {directions.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={1}>
          <TextField
              label={t("Suspicious")}
              select
              fullWidth
              SelectProps={{ native: true }}
              value={filter.Suspicious}
              onChange={(e) => onUpdateFilter("Suspicious", e.target.value)}
          >
            <option></option>
            {suspiciousTypes.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
            ))}
          </TextField>
        </Grid>
      </FilterBar>
    </Grid>
  );
};

export default Filter;
